import React from "react";
import { useCentralStore } from "@/dentlab/src/store/Central";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@/dentlab/src/components/ui/card";
import { Label } from "@/dentlab/src/components/ui/label";
import { Checkbox } from "@/dentlab/src/components/ui/checkbox";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/dentlab/src/components/ui/select";
import {
	ToggleGroup,
	ToggleGroupItem,
} from "@/dentlab/src/components/ui/toggle-group";
import {
	SumexXmlVersion,
	sumexXmlVersionOptions,
} from "@/dentlab/src/hooks/useExportXml/sumex-xml-version";
import {
	PatientNameFormat,
	patientNameFormatOptions,
	SammelrechnungenSortingOptions,
	srgSortingOptions,
} from "@/dentlab/src/types/enums";

export const KundeDokumenteneinstellungenPage: React.FC = () => {
	const { client, handleImmediateClientFieldUpdate, practitionersLookup } =
		useCentralStore((state) => ({
			client: state.client,
			handleImmediateClientFieldUpdate:
				state.handleImmediateClientFieldUpdate,
			practitionersLookup: state.practitionersLookup,
		}));

	// Check if the current client has practitioners
	const hasPractitioners = client?.id
		? Object.values(practitionersLookup).some(
				(practitioner) => practitioner.client_id === client.id
			)
		: false;

	if (!client) {
		return (
			<div className="p-6">
				<div className="text-gray-500">Kein Kunde ausgewählt</div>
			</div>
		);
	}

	return (
		<div className="p-6">
			<div className="max-w-6xl">
				<h1 className="text-2xl font-semibold text-gray-900 mb-6">
					Dokumente
				</h1>
				<div className="space-y-6">
					{/* Dokumenteinstellungen Section */}
					<Card>
						<CardHeader>
							<CardTitle>Dokumenteinstellungen</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="flex items-center space-x-2">
								<Checkbox
									id="is_pt_name_in_doc_name"
									checked={
										client.is_pt_name_in_doc_name
											? true
											: false
									}
									onCheckedChange={(checked) =>
										handleImmediateClientFieldUpdate(
											"is_pt_name_in_doc_name",
											checked as boolean
										)
									}
								/>
								<Label
									htmlFor="is_pt_name_in_doc_name"
									className="cursor-pointer"
								>
									Patientenname im Dateinamen von KV, LS, GS
								</Label>
							</div>

							<div className="flex items-center space-x-2">
								<Checkbox
									id="is_pt_name_in_xml_name"
									checked={
										client.is_pt_name_in_xml_name
											? true
											: false
									}
									onCheckedChange={(checked) =>
										handleImmediateClientFieldUpdate(
											"is_pt_name_in_xml_name",
											checked as boolean
										)
									}
								/>
								<Label
									htmlFor="is_pt_name_in_xml_name"
									className="cursor-pointer"
								>
									Patientenname im Dateinamen von SUVA XML
									Dateien
								</Label>
							</div>

							<div>
								<Label htmlFor="sumex-xml-version">
									XML-Version für SV-Lieferscheine (ELNF)
								</Label>
								<Select
									value={
										client.sumex_xml_version ?? SumexXmlVersion.V450
									}
									onValueChange={(value) => {
										handleImmediateClientFieldUpdate(
											"sumex_xml_version",
											value
										);
									}}
								>
									<SelectTrigger
										id="sumex-xml-version"
										className="mt-1"
										aria-label="XML-Version für SV-Lieferscheine"
										data-testid="client-sumex-xml-version-select"
									>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{sumexXmlVersionOptions.map((option) => (
											<SelectItem
												key={option.value}
												value={option.value}
												data-testid={`client-sumex-xml-version-option-${option.value}`}
											>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<p className="mt-1 text-sm text-muted-foreground">
									Wählen Sie 5.00 nur, wenn die Praxissoftware des
									Kunden den neuen Standard verlangt.
								</p>
							</div>

							<div className="flex items-center space-x-2">
								<Checkbox
									id="is_send_email_on_share"
									checked={
										client.is_send_email_on_share !== false
									}
									onCheckedChange={(checked) =>
										handleImmediateClientFieldUpdate(
											"is_send_email_on_share",
											checked as boolean
										)
									}
								/>
								<Label
									htmlFor="is_send_email_on_share"
									className="cursor-pointer"
								>
									E-Mail-Benachrichtigung beim Teilen senden
								</Label>
							</div>
						</CardContent>
					</Card>

					{/* Sammelrechnungen Section */}
					<Card>
						<CardHeader>
							<CardTitle>Sammelrechnungen</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="flex items-center space-x-2">
								<Checkbox
									id="is_inv_w_job_title"
									checked={
										client.is_inv_w_job_title ? true : false
									}
									onCheckedChange={(checked) =>
										handleImmediateClientFieldUpdate(
											"is_inv_w_job_title",
											checked as boolean
										)
									}
								/>
								<Label
									htmlFor="is_inv_w_job_title"
									className="cursor-pointer"
								>
									Auftragstitel auf Sammelrechnungen
								</Label>
							</div>

							<div className="flex items-center space-x-2">
								<Checkbox
									id="is_inv_w_pat_data"
									checked={
										client.is_inv_w_pat_data ? true : false
									}
									onCheckedChange={(checked) =>
										handleImmediateClientFieldUpdate(
											"is_inv_w_pat_data",
											checked as boolean
										)
									}
								/>
								<Label
									htmlFor="is_inv_w_pat_data"
									className="cursor-pointer"
								>
									Patient auf Sammelrechnungen
								</Label>
							</div>

							<div>
								<Label htmlFor="patient-name-format">
									Schreibweise Patientenname auf
									Sammelrechnungen
								</Label>
								<Select
									value={
										client.patient_name_format?.toString() ||
										PatientNameFormat.LAST_NAME_COMMA_FIRST_NAME.toString()
									}
									onValueChange={(value) => {
										handleImmediateClientFieldUpdate(
											"patient_name_format",
											Number(value)
										);
									}}
									disabled={!client.is_inv_w_pat_data}
								>
									<SelectTrigger
										id="patient-name-format"
										className="mt-1"
									>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{patientNameFormatOptions.map(
											(option) => (
												<SelectItem
													key={option.value}
													value={option.value.toString()}
												>
													{option.label}
												</SelectItem>
											)
										)}
									</SelectContent>
								</Select>
							</div>

							<div>
								<Label htmlFor="sammelrechnungen-sorting">
									Sortierung der Zeilen auf Sammelrechnungen
								</Label>
								<Select
									value={
										client.srg_sorting_option?.toString() ||
										SammelrechnungenSortingOptions.PATIENT_LAST_NAME.toString()
									}
									onValueChange={(value) => {
										handleImmediateClientFieldUpdate(
											"srg_sorting_option",
											Number(value)
										);
									}}
								>
									<SelectTrigger
										id="sammelrechnungen-sorting"
										className="mt-1"
									>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{srgSortingOptions.map((option) => (
											<SelectItem
												key={option.value}
												value={option.value.toString()}
												disabled={option.disabled({
													is_inv_w_pat_data:
														client.is_inv_w_pat_data ??
														false,
													is_inv_w_job_title:
														client.is_inv_w_job_title ??
														false,
													patient_name_format:
														client.patient_name_format ??
														PatientNameFormat.LAST_NAME_COMMA_FIRST_NAME,
												})}
											>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className="flex items-center space-x-2">
								<Checkbox
									id="is_srg_grouped_by_prac"
									checked={
										client.is_srg_grouped_by_prac
											? true
											: false
									}
									onCheckedChange={(checked) =>
										handleImmediateClientFieldUpdate(
											"is_srg_grouped_by_prac",
											checked as boolean
										)
									}
									disabled={!hasPractitioners}
								/>
								<Label
									htmlFor="is_srg_grouped_by_prac"
									className={`cursor-pointer ${
										!hasPractitioners ? "text-gray-400" : ""
									}`}
								>
									Nach Behandler gruppieren
								</Label>
							</div>
						</CardContent>
					</Card>

					{/* Versicherungsinformationen Section */}
					<Card>
						<CardHeader>
							<CardTitle>Versicherungsinformationen</CardTitle>
						</CardHeader>
						<CardContent>
							<div>
								<div className="text-sm font-medium mb-2">
									Versicherungsinformationen auf KV/LS
								</div>
								<ToggleGroup
									type="single"
									value={
										client.is_show_insurance_on_pdf === null
											? "_default_"
											: client.is_show_insurance_on_pdf ===
												  true
												? "true"
												: "false"
									}
									onValueChange={(value) => {
										handleImmediateClientFieldUpdate(
											"is_show_insurance_on_pdf",
											value === "_default_"
												? null
												: value === "true"
													? true
													: false
										);
									}}
								>
									<ToggleGroupItem
										value="true"
										variant="outline"
									>
										Ja
									</ToggleGroupItem>
									<ToggleGroupItem
										value="false"
										variant="outline"
									>
										Nein
									</ToggleGroupItem>
									<ToggleGroupItem
										value="_default_"
										variant="outline"
									>
										Labor-Standard
									</ToggleGroupItem>
								</ToggleGroup>
							</div>
						</CardContent>
					</Card>

					{/* Dokument-Einstellungen Section */}
					<Card>
						<CardHeader>
							<CardTitle>Steuerausweisung</CardTitle>
						</CardHeader>
						<CardContent>
							<div>
								<div className="text-sm font-medium mb-2">
									Steuerausweisung
								</div>
								<ToggleGroup
									type="single"
									value={
										client.vat_display_override ||
										"_default_"
									}
									onValueChange={(value) => {
										handleImmediateClientFieldUpdate(
											"vat_display_override",
											value === "_default_" ? null : value
										);
									}}
									className="justify-start"
								>
									<ToggleGroupItem
										value="inclusive"
										variant="outline"
									>
										Inkl. MwSt.
									</ToggleGroupItem>
									<ToggleGroupItem
										value="exlusive"
										variant="outline"
									>
										Exkl. MwSt.
									</ToggleGroupItem>
									<ToggleGroupItem
										value="_default_"
										variant="outline"
									>
										Labor-Standard
									</ToggleGroupItem>
								</ToggleGroup>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	);
};
